import React from 'react';
import { createLogger } from '../utils/logger';
const logger = createLogger('ForceUpdateModal');

import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Image,
  Linking,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../utils/responsive';

interface ForceUpdateModalProps {
  visible: boolean;
  version: string;
  releaseNotes?: string;
  updateUrl?: string;
}

const ForceUpdateModal: React.FC<ForceUpdateModalProps> = ({
  visible,
  version,
  releaseNotes,
  updateUrl,
}) => {
  const handleUpdate = async () => {
    const url =
      updateUrl ||
      (Platform.OS === 'ios'
        ? 'https://apps.apple.com/app/id6756896149'
        : 'https://play.google.com/store/apps/details?id=com.myfinalkeyapp');

    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      }
    } catch (error) {
      logger.error('Failed to open store:', error);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      statusBarTranslucent={true}
    >
      <View style={styles.overlay}>
        <View
          style={styles.modalContainer}
          accessibilityRole="alert"
          accessibilityLabel={`Update Required. A critical update is required to continue using MyFinalKey. Please update to version ${version}.`}
        >
          <View style={styles.iconContainer} accessibilityElementsHidden={true}>
            <Text style={styles.icon}>🔄</Text>
          </View>

          <Text style={styles.title} accessibilityRole="header">Update Required</Text>

          <Text style={styles.description}>
            A critical update is required to continue using MyFinalKey. Please
            update to version {version}.
          </Text>

          {releaseNotes && (
            <View style={styles.releaseNotesContainer}>
              <Text style={styles.releaseNotesTitle} accessibilityRole="header">What's New:</Text>
              <Text style={styles.releaseNotesText}>{releaseNotes}</Text>
            </View>
          )}

          <TouchableOpacity
            style={styles.updateButton}
            onPress={handleUpdate}
            accessibilityRole="button"
            accessibilityLabel="Update Now"
            accessibilityHint="Double tap to open the app store and update the app"
          >
            <Text style={styles.updateButtonText}>Update Now</Text>
          </TouchableOpacity>

          <Text style={styles.footerNote}>
            This update includes important security and performance improvements.
          </Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(20),
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    padding: ms(30),
    width: '100%',
    maxWidth: s(400),
    alignItems: 'center',
  },
  iconContainer: {
    width: s(80),
    height: s(80),
    borderRadius: ms(40),
    backgroundColor: '#E8F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  icon: {
    fontSize: ms(40, 0.3),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
    textAlign: 'center',
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    marginBottom: ms(20),
  },
  releaseNotesContainer: {
    width: '100%',
    backgroundColor: '#F8F9FA',
    borderRadius: ms(12),
    padding: ms(15),
    marginBottom: ms(20),
  },
  releaseNotesTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(8),
  },
  releaseNotesText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(22, 0.3),
  },
  updateButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(40),
    width: '100%',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  updateButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  footerNote: {
    fontSize: ms(12, 0.3),
    color: '#999',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
});

export default ForceUpdateModal;
